// Odoo's own state of the quote (`erpPricingStatus` on the sale order), and
// the rule deciding when an action that rewrites prices has to be confirmed
// first. Kept out of the view so each rule is unit-testable on its own.
//
// This is a SECOND, independent field from our `pricingStatus` (NEW,
// PRICING_REQUIRED, POSTED, CANCELLED). The two are never merged: the POSTED
// filter and the status badge keep reading `pricingStatus` alone.

/**
 * The two states in which Odoo still considers the quote open for pricing.
 * Mirrors `OPEN_FOR_PRICING_ERP_STATES` in the backend's
 * `src/models/saleOrder.ts` — if that list changes, this one follows.
 */
export const OPEN_FOR_PRICING_ERP_STATES = ['draft', 'draft_quote'];

/**
 * Odoo's own label for each state, for use in the confirmation copy and the
 * header fact. Taken from the `state` selection on `sale.order` in Odoo, which
 * is the only authority for these — several differ from what you would guess
 * from the code (`pi` is Partially Invoiced, not proforma; `sale` is Order
 * Placed, not Sales order).
 */
export const ERP_STATUS_LABELS: Record<string, string> = {
  draft: 'Quotation',
  sent: 'Quotation Sent',
  sale: 'Order Placed',
  done: 'Done',
  cancel: 'Cancelled',
  mop: 'Production Planned',
  pip: 'Production In Progress',
  partial: 'Partially Shipped',
  shipped: 'Shipped',
  pi: 'Partially Invoiced',
  inv: 'Invoiced',
  lost: 'Lost',
  draft_quote: 'Revised Quotation',
  revised: 'Revised Order',
};

/**
 * Whether Odoo still treats the order as open for pricing.
 *
 * A missing field counts as open ON PURPOSE: it keeps today's behaviour on any
 * deployment where the backend does not yet send the field, and a stale cached
 * order never blocks a refresh.
 */
export const isOpenForPricing = (erpPricingStatus?: string | null): boolean =>
  !erpPricingStatus || OPEN_FOR_PRICING_ERP_STATES.includes(erpPricingStatus);

/**
 * Whether an action that rewrites prices must be confirmed by the user first.
 * This is the whole condition — our own `pricingStatus` plays no part in it.
 */
export const needsErpConfirmation = (erpPricingStatus?: string | null): boolean =>
  !isOpenForPricing(erpPricingStatus);

/**
 * Odoo's label for a state, falling back to the raw value so a state added in
 * Odoo before it is added here still reads as something rather than blank.
 */
export const erpStatusLabel = (erpPricingStatus?: string | null): string =>
  (erpPricingStatus && ERP_STATUS_LABELS[erpPricingStatus]) || erpPricingStatus || '';
